import React, { useRef, useState, useEffect } from "react";
import { Card, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";

interface Document {
  id: number;
  documentType: string;
  senderKeyName: string;
  additionalComment: string;
  totalPages: number;
  uploadedBy: string;
  uploadedOn: string;
  totalPageCount: number; // ensure this is in your data
  documentTypeName: string;
  additionalComments?: string;
  fileAction?: string;
  headerModifiedByName?: string;
  headerUpdatedDate?: string;
}

interface Props {
  documents: any[];
  onCardAction: (document: any, actionType: "row" | "file" | "download") => void;
  sortBySender: boolean;
}

const TextWithTooltip: React.FC<{
  text: string;
  className?: string;
  maxWidth?: string | number;
}> = ({ text, className, maxWidth = "100%" }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }

    const elements = document.querySelectorAll(".record_listbox.fade-in-left");
    elements.forEach((el) => {
      el.classList.remove("fade-in-left");
      void (el as HTMLElement).offsetWidth;
      el.classList.add("fade-in-left");
    });
  }, [text]);

  const content = (
    <span
      ref={textRef}
      className={`d-inline-block text-truncate ${className || ""}`}
      style={{ maxWidth }}
    >
      {text || "-"}
    </span>
  );

  return isOverflowing ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  ) : (
    content
  );
};

// 🔹 Animated Counter Component
const AnimatedCounter: React.FC<{ target: number }> = ({ target }) => {
  const [count, setCount] = useState(target);

  useEffect(() => {
    let start = target + 20; // only 10 digits above
    let end = target;
    let duration = 800; // total duration in ms
    let stepTime = Math.max(Math.floor(duration / (start - end || 1)), 50);

    let current = start;
    setCount(current);

    const timer = setInterval(() => {
      current -= 1;
      setCount(current);
      if (current <= end) {
        clearInterval(timer);
        setCount(end);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [target]);

  return <>{count}</>;
};

const DocumentCards: React.FC<Props> = ({
  documents,
  onCardAction,
  sortBySender,
}) => {
  const handleCardClick = (document: Document) => {
    onCardAction(document, "row");
  };

  const handleSingleFileDownload = (e: React.MouseEvent, document: Document) => {
    e.stopPropagation();
    onCardAction(document, "download");
  };

  const handleFileActionClick = (e: React.MouseEvent, document: Document) => {
    e.stopPropagation();
    onCardAction(document, "file");
  };

  // dynamic col size based on document count
  let colProps: { md: number; lg: number } = { md: 4, lg: 4 };
  if (documents.length === 1) {
    colProps = { md: 6, lg: 6 }; // 50%
  } else if (documents.length === 2) {
    colProps = { md: 6, lg: 6 }; // 50%
  } else {
    colProps = { md: 4, lg: 4 }; // 3+ docs → 33%
  }

  return (
    <Row className="g-3">
      {documents.map((doc) => (
        <Col key={doc.id} {...colProps} className="record_listbox fade-in-left">
          <Card
            className="h-100 shadow-sm border-0 card_rec"
            onClick={() => {
              if (
                doc.documentTypeName ===
                "110 Form (QME Appointment Notification)"
              ) {
                return;
              }
              handleCardClick(doc);
            }}
            style={{ cursor: "pointer" }}
          >
            <Card.Body className="p-0 rec_data_listaa">
              <div className="d-flex p-2">
                <span className="title_tt">Document Type</span>
                <TextWithTooltip
                  text={doc.documentTypeName}
                  className="rec_output rec_outputName"
                />
              </div>

              {sortBySender ? (
                <div className="d-flex p-2">
                  <span className="title_tt">Document Category</span>
                  <TextWithTooltip
                    text={doc.documentCategory}
                    className="rec_output"
                  />
                </div>
              ) : (
                <div className="d-flex p-2">
                  <span className="title_tt">Sender</span>
                  <TextWithTooltip
                    text={doc.senderKeyName}
                    className="rec_output"
                  />
                </div>
              )}

              <div className="d-flex p-2">
                <span className="title_tt">Additional Comment</span>
                <TextWithTooltip
                  text={doc.additionalComments || "-"}
                  className="rec_output"
                />
              </div>

              <div className="d-flex p-2">
                <span className="title_tt">Total Pages</span>
                <span className="text-danger fw-bold rec_output">
                  <AnimatedCounter target={doc.totalPageCount} />
                </span>
              </div>

              {(doc.documentTypeName === "Transcription" ||
                doc.documentTypeName === "Draft Transcription") && (
                <div className="d-flex p-2">
                  <span className="title_tt">Total Lines</span>
                  <span className="text-danger fw-bold rec_output">
                    <AnimatedCounter target={doc?.totalLineCount} />
                  </span>
                </div>
              )}

              <div className="d-flex p-2">
                <span className="title_tt">File</span>
                {doc.documentTypeName ===
                "110 Form (QME Appointment Notification)" ? (
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleSingleFileDownload(e, doc)}
                    role="button"
                  >
                    <span className="material-icons mx-1 color_brand text-dark rec_output">
                      download
                    </span>
                  </span>
                ) : (
                  <span
                    className="attachfile d-inline-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleFileActionClick(e, doc)}
                    role="button"
                  >
                    <span className="material-icons mx-1 color_brand text-dark rec_output">
                      attachment
                    </span>
                    {doc.fileAction}
                  </span>
                )}
              </div>

              <div className="bottom_pos">
                <div className=" d-flex lastColor_uploaded p-2">
                  <span className="title_tt">Uploaded By</span>
                  <TextWithTooltip
                    text={doc.headerModifiedByName || "-"}
                    className="rec_output"
                  />
                </div>
                <div className=" d-flex lastColor_uploaded p-2">
                  <span className="title_tt">Uploaded On</span>
                  <TextWithTooltip
                    text={doc.headerUpdatedDate || "-"}
                    className="rec_output"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default DocumentCards;
